/**
 * 文件生成模块
 * 处理 API 文件和类型定义文件的生成
 */
import * as fs from 'fs';
import * as path from 'path';
import * as prettier from 'prettier';
import { compile } from 'json-schema-to-typescript';
import Store from '@/store';
import type { AutoRequestOptions } from '@/core/snapshot';

/**
 * 确保目录存在，如果不存在则递归创建
 * @param filePath 文件路径
 */
function ensureDirectoryExists(filePath: string) {
  const dirname = path.dirname(filePath);
  if (!fs.existsSync(dirname)) {
    fs.mkdirSync(dirname, { recursive: true });
    console.log(`📁 创建目录: ${dirname}`);
  }
}

/**
 * 生成并写入文件
 * @param template - API 模板代码
 * @param defines - 类型定义对象
 * @param jsdocDefines - JSDoc 定义配置
 * @param outputPath - 输出路径
 * @param options - 配置选项
 * @returns Promise with write function
 */
export const generateFile = (
  template: any,
  defines: any,
  jsdocDefines = { content: '', prefix: '', endifx: '' },
  outputPath = '',
  options: AutoRequestOptions
) => {
  return new Promise((resolve) => {
    resolve({
      write: async () => {
        const apiDefineStream = defines;
        delete apiDefineStream.preDefine;

        // 生成接口文件路径
        const apiFilePath = `${outputPath}${options.filename}${Store.getFileType()}`;
        
        // 确保目录存在
        ensureDirectoryExists(apiFilePath);
        
        // 写入接口文件
        fs.writeFileSync(
          apiFilePath,
          await prettier.format(template, { semi: false, parser: 'typescript' })
        );

        // 有 loggerPath 才输出错误日志
        if (Store.errorPath && Store.logFilename) {
          const errorFilePath = `${Store.errorPath}/${Store.logFilename}`;
          ensureDirectoryExists(errorFilePath);
          fs.writeFileSync(
            errorFilePath,
            await prettier.format(Store.print(), { semi: false, parser: 'json' })
          );
        }

        if (Store.isJsDoc) {
          // 2.2.0 添加jsdoc版本的定义文件
          compile(apiDefineStream as any, 'Api').then(async (ts) => {
            const typeFilePath = `${outputPath}${options.filename}.types.ts`;
            ensureDirectoryExists(typeFilePath);
            fs.writeFileSync(
              typeFilePath,
              await prettier.format(jsdocDefines.prefix + ts + jsdocDefines.content + jsdocDefines.endifx, {
                semi: false,
                parser: 'typescript',
              })
            );
          });
        } else if (Store.isTypeScript) {
          // ts模式才生成ts文件
          compile(apiDefineStream as any, 'Api').then((ts) => {
            const defineFilePath = `${outputPath}${options.filename}.define${Store.getFileType()}`;
            ensureDirectoryExists(defineFilePath);
            fs.writeFileSync(defineFilePath, ts);
          });
        }

        console.log(`接口文件已经生成，一共生成${options.total}个接口`);
        if (Store.errorUrl.length > 0) {
          console.log(
            `接口文档检测到有${Store.errorUrl.length}个问题，可以配置 loggerPath 参数（如 './logs/errors.json'）输出日志检查错误`
          );
        }
      },
      template,
    });
  });
};
